import { DateInput } from "../components/DateInput";
import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useSettings, formatMoney } from "../lib/settings";
import { AlertTriangle, Plus } from "lucide-react";
import { useAuth } from "../lib/auth";
import { RecordPaymentModal } from "../components/RecordPaymentModal";

import { tr, lbl, trText } from "../lib/i18n";
export default function PaymentsPage() {
  const { settings } = useSettings();
  const { can } = useAuth();
  const [recording, setRecording] = useState(false);
  const [reload, setReload] = useState(0);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filters, setFilters] = useState({ type: "", date_from: "", date_to: "" });
  const [types, setTypes] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
        const [tx, sm] = await Promise.all([
          api.get("/payments", { params }),
          api.get("/payments/summary"),
        ]);
        if (!alive) return;
        setItems(tx.data.items || []);
        setTypes(tx.data.types || []);
        setSummary(sm.data.by_type || []);
      } catch (e) {
        if (!alive) return;
        setError(formatApiError(e));
        setItems([]); setSummary([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [filters, reload]);

  return (
    <div className="space-y-5" data-testid="payments-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div><div className="eyebrow">{tr("x.ledger")}</div><h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{tr("x.payments")}</h1><p className="text-sm text-[var(--gray-400)] mt-1">{tr("x.all_transactions_are_append_only_correct")}</p></div>
        {can("payments", "create") && (
          <button className="btn-primary" onClick={() => setRecording(true)} data-testid="record-payment-btn"><Plus size={14} /> {tr("x.record_payment")}</button>
        )}
      </div>
      {recording && <RecordPaymentModal onClose={() => setRecording(false)} onSaved={() => { setRecording(false); setReload((n) => n + 1); }} />}

      {error && (
        <div className="panel p-4 flex items-start gap-3 border-l-4 border-red-500" data-testid="payments-error">
          <AlertTriangle size={18} className="text-red-500 mt-0.5" />
          <div>
            <div className="font-semibold text-sm">{tr("x.could_not_load_payments")}</div>
            <div className="text-xs text-[var(--gray-400)] mt-0.5">{error}</div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {summary.map((s) => (
          <div key={s._id} className="panel p-4" data-testid={`summary-${s._id}`}>
            <div className="eyebrow mb-1">{lbl(s._id)}</div>
            <div className="text-xs text-[var(--gray-400)]">{tr("x.in")} <span className="tabular text-[hsl(var(--foreground))]">{formatMoney(s.total_in, settings)}</span></div>
            <div className="text-xs text-[var(--gray-400)]">{tr("x.out")} <span className="tabular text-[hsl(var(--foreground))]">{formatMoney(s.total_out, settings)}</span></div>
          </div>
        ))}
      </div>

      <div className="panel p-3 flex gap-2 flex-wrap">
        <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="h-9 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
          <option value="">{tr("x.all_types")}</option>
          {types.map((t) => <option key={t} value={t}>{lbl(t)}</option>)}
        </select>
        <DateInput type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-9 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
        <DateInput type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-9 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full dense-table">
          <thead><tr><th>{tr("x.date")}</th><th>{tr("x.type")}</th><th>{tr("x.direction")}</th><th>{tr("x.amount_2")}</th><th>{tr("x.method")}</th><th>{tr("x.reference")}</th><th>{tr("x.notes")}</th></tr></thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id}>
                <td className="text-xs mono">{t.date}</td>
                <td>{lbl(t.type)}</td>
                <td><span className={`pill ${t.direction === "In" ? "pill-available" : "pill-maint"}`}>{lbl(t.direction)}</span></td>
                <td className="tabular font-semibold">{formatMoney(t.amount, settings)}</td>
                <td className="text-xs">{lbl(t.method)}</td>
                <td className="mono text-xs">{t.reference || "—"}</td>
                <td className="text-xs">{trText(t.notes) || "—"}</td>
              </tr>
            ))}
            {!loading && items.length === 0 && !error && (
              <tr><td colSpan={7} className="text-center py-8 text-[var(--gray-400)]">{tr("x.no_transactions_yet")}</td></tr>
            )}
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-[var(--gray-400)]">{tr("x.loading")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
